import { memo, forwardRef, useRef, useEffect, useState, useLayoutEffect } from 'react';
// @mui
import Box from '@mui/material/Box';
//
import { CasStyledRootScrollbar, CasStyledScrollbar } from './styles';
import { ICasScrollbarProps } from './types';

// ----------------------------------------------------------------------

const CasScrollbar = forwardRef<HTMLDivElement, ICasScrollbarProps>(
  ({ children, sx, showTopScrollbar, ...other }, ref) => {
    const userAgent = typeof navigator === 'undefined' ? 'SSR' : navigator.userAgent;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    );

    const topRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const [scrollWidth, setScrollWidth] = useState(0);

    // Assign forwarded ref to bottomRef
    useEffect(() => {
      if (typeof ref === 'function') {
        ref(bottomRef.current);
      } else if (ref) {
        ref.current = bottomRef.current;
      }
    }, [ref]);

    useLayoutEffect(() => {
      const el = bottomRef.current;
      if (!el) return;

      const updateWidth = () => {
        setScrollWidth(el.scrollWidth);
      };

      updateWidth(); // initial run

      const resizeObserver = new ResizeObserver(() => {
        updateWidth();
      });

      resizeObserver.observe(el);
      // eslint-disable-next-line consistent-return
      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // Sync scrollLeft between top and bottom scrollbars
    useEffect(() => {
      const top = topRef.current;
      const bottom = bottomRef.current;
      if (!top || !bottom) return;

      const handleTopScroll = () => {
        bottom.scrollLeft = top.scrollLeft;
      };
      const handleBottomScroll = () => {
        top.scrollLeft = bottom.scrollLeft;
      };

      top.addEventListener('scroll', handleTopScroll);
      bottom.addEventListener('scroll', handleBottomScroll);

      // eslint-disable-next-line consistent-return
      return () => {
        top.removeEventListener('scroll', handleTopScroll);
        bottom.removeEventListener('scroll', handleBottomScroll);
      };
    }, []);

    if (isMobile) {
      return (
        <Box ref={bottomRef} sx={{ overflow: 'auto', ...sx }} {...other}>
          {children}
        </Box>
      );
    }

    return (
      <CasStyledRootScrollbar>
        {showTopScrollbar && (
          <CasStyledScrollbar
            sx={{
              height: 16,
              '& .simplebar-content': {
                height: '1px',
                visibility: 'hidden',
              },
              '& .simplebar-track.simplebar-vertical': {
                display: 'none',
              },
              ...sx,
            }}
            scrollableNodeProps={{ ref: topRef }}
            clickOnTrack={false}
            {...other}
          >
            <Box width={scrollWidth} height={1} />
          </CasStyledScrollbar>
        )}

        <CasStyledScrollbar
          scrollableNodeProps={{
            ref: bottomRef,
          }}
          clickOnTrack={false}
          sx={sx}
          {...other}
        >
          {children}
        </CasStyledScrollbar>
      </CasStyledRootScrollbar>
    );
  }
);

export default memo(CasScrollbar);
